import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ChipToggle, EmptyState, ErrorState, PageHead, SkeletonList, StatusPill } from "@ui/react";
import { CalendarIcon } from "@ui/react/icons";
import { listBookings, getDirectBookingSettings } from "../../lib/api/contractor";
import { parseBookingStatus } from "../../lib/types/enums";
import type { Booking, BookingFilter } from "../../lib/types/models";
import { km, money, zonedDateTime } from "../../lib/format";
import { useGeolocation } from "../../lib/useGeolocation";
import { MarketplaceModeNotice, useBookableMode } from "../booking-settings/ModeGate";
import { errorMessage } from "../../lib/errorMessage";

interface Tab {
  id: BookingFilter;
  label: string;
  emptyTitle: string;
  emptyBody: string;
}

/* The four server-side filters, in the order a contractor works through them:
 * decide on requests first, then today's work, then the diary, then history. */
const TABS: Tab[] = [
  {
    id: "inbox",
    label: "Requests",
    emptyTitle: "No requests waiting",
    emptyBody: "New booking requests from customers land here for you to accept or decline.",
  },
  {
    id: "today",
    label: "Today",
    emptyTitle: "Nothing on today",
    emptyBody: "Bookings scheduled in the next 24 hours show up here.",
  },
  {
    id: "upcoming",
    label: "Upcoming",
    emptyTitle: "Nothing booked yet",
    emptyBody: "Accepted and paid bookings still to come will be listed here.",
  },
  {
    id: "past",
    label: "Past",
    emptyTitle: "No past bookings",
    emptyBody: "Completed, declined and cancelled bookings stay here for your records.",
  },
];

export function BookingsInboxScreen() {
  const bookable = useBookableMode();
  const geo = useGeolocation();
  const [filter, setFilter] = useState<BookingFilter>("inbox");
  const [reloadKey, setReloadKey] = useState(0);
  const [zone, setZone] = useState("Africa/Johannesburg");
  const [state, setState] = useState<{
    loading: boolean;
    error: unknown;
    data: Booking[] | null;
  }>({ loading: true, error: null, data: null });

  useEffect(() => {
    if (!bookable) return;

    let ignore = false;
    getDirectBookingSettings()
      .then((s) => {
        if (!ignore) setZone(s.bookingTimezone || "Africa/Johannesburg");
      })
      .catch(() => {
        // Silently fall back to default zone on error
        if (!ignore) setZone("Africa/Johannesburg");
      });

    return () => {
      ignore = true;
    };
  }, [bookable]);

  useEffect(() => {
    if (!bookable) return;

    let ignore = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    listBookings(filter, geo.position?.lat, geo.position?.lng)
      .then((response) => {
        // Switching tabs quickly must not let a slow earlier filter's result
        // paint over the one the contractor is actually looking at.
        if (!ignore) setState({ loading: false, error: null, data: response.bookings ?? [] });
      })
      .catch((error: unknown) => {
        if (!ignore) setState({ loading: false, error, data: null });
      });

    return () => {
      ignore = true;
    };
  }, [bookable, filter, reloadKey, geo.position]);

  if (!bookable) {
    return (
      <MarketplaceModeNotice
        what="Bookings"
        body="Bookings only appear once you're taking direct bookings. Add a service and your availability first, then switch your operating mode from your profile."
      />
    );
  }

  const tab = TABS.find((candidate) => candidate.id === filter) ?? TABS[0]!;

  return (
    <div className="stack stack--loose">
      <PageHead title="Bookings" />

      <div className="chip-scroller" role="group" aria-label="Filter bookings">
        {TABS.map((candidate) => (
          <ChipToggle
            key={candidate.id}
            pressed={candidate.id === filter}
            onToggle={() => setFilter(candidate.id)}
          >
            {candidate.label}
          </ChipToggle>
        ))}
      </div>

      {state.loading ? (
        <div role="status">
          <span className="sr-only">Loading {tab.label.toLowerCase()} bookings</span>
          <SkeletonList rows={3} height="7rem" />
        </div>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={() => setReloadKey((key) => key + 1)} />
      ) : !state.data || state.data.length === 0 ? (
        <EmptyState icon={<CalendarIcon />} title={tab.emptyTitle}>
          {tab.emptyBody}
        </EmptyState>
      ) : (
        <ul className="stack">
          {state.data.map((booking) => (
            <li key={booking.bookingId}>
              <BookingRow booking={booking} zone={zone} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BookingRow({ booking, zone }: { booking: Booking; zone: string }) {
  const status = parseBookingStatus(booking.status);
  const title = [booking.serviceName, booking.variantLabel].filter(Boolean).join(" — ");

  return (
    <Link to={`/bookings/${booking.bookingId}`} className="row">
      <div className="stack stack--tight">
        {/* `.row__top` and not a wrapping cluster: a long service name used to
            push the pill onto its own line while a short one kept it top-right,
            so three rows in a list had two different layouts. */}
        <div className="row__top">
          <span className="row__title truncate">{title || "Booking"}</span>
          <StatusPill kind="booking" status={status} />
        </div>
        {/* RFC 3339 instant with timezone label. */}
        <span className="muted">{zonedDateTime(booking.scheduledStartAt, zone)}</span>
        {/* `address` and `distance` never both arrive (contractor-api withholds
            the address until acceptance and only computes distance while it
            does) — this is an either/or, not a fallback for a blank field. */}
        {booking.address ? (
          <span className="dim">{booking.address}</span>
        ) : booking.distance ? (
          <span className="dim">
            {km(booking.distance)} away
            {status === "requested" ? " · The address is shared once you accept." : ""}
          </span>
        ) : null}
        <span className="amount">{money(booking.totalAmount, booking.totalCurrency)}</span>
      </div>
    </Link>
  );
}
